// Edit-or-add modal for RFQ line items. Used in two modes:
//   - 'edit': pre-fills from an existing line, PATCH /api/rfqs/line-items/:id
//   - 'create': blank form, POST /api/rfqs/:rfqPublicId/line-items
//
// All fields the broker can edit/add are here in one place — partNumber, qty,
// condition, cert, etc. Stage and lineNumber stay server-managed (not editable
// here). Save invalidates the RFQ query so the parent re-renders with the new
// data.

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

const CONDITIONS = ['NE', 'NS', 'FN', 'OH', 'SV', 'AR', 'RP'] as const;
const CERTS = ['faa_8130_3', 'easa_form_1', 'dual_release', 'coc', 'none'] as const;
const CERT_LABEL: Record<(typeof CERTS)[number], string> = {
  faa_8130_3: 'FAA 8130-3',
  easa_form_1: 'EASA Form 1',
  dual_release: 'Dual release (FAA + EASA)',
  coc: 'CoC (manufacturer)',
  none: 'No cert required',
};

export interface LineItemInitial {
  // Required for 'edit' mode (the line's public id).
  publicId?: string;
  lineNumber?: number;
  partNumber: string;
  altPartNumber: string | null;
  description: string | null;
  manufacturer: string | null;
  aircraftPlatform: string | null;
  qty: number;
  uom: string;
  conditionRequested: string | null;
  certRequested: string | null;
  isAog: boolean;
  notes: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  mode: 'edit' | 'create';
  rfqId: string;            // RFQ public_id (used in 'create'; also for query invalidation)
  initial?: LineItemInitial; // required for 'edit'
}

function emptyInitial(): LineItemInitial {
  return {
    partNumber: '',
    altPartNumber: null,
    description: null,
    manufacturer: null,
    aircraftPlatform: null,
    qty: 1,
    uom: 'EA',
    conditionRequested: null,
    certRequested: null,
    isAog: false,
    notes: null,
  };
}

export function LineItemEditModal({ open, onClose, mode, rfqId, initial }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const dismiss = useModalDismiss(onClose);

  const [form, setForm] = useState<LineItemInitial>(emptyInitial());

  // Re-seed the form whenever the modal opens OR the line being edited changes.
  // Also resets confirmDelete and clears any stale mutation errors — otherwise:
  //   (a) a still-armed delete from line A could fire against line B when the
  //       broker clicks Edit on a different line WITHOUT closing the modal,
  //   (b) an old error message from a previous edit would show on the new line.
  useEffect(() => {
    if (!open) return;
    setForm(initial ?? emptyInitial());
    setConfirmDelete(false);
    save.reset();
    del.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        partNumber: form.partNumber.trim(),
        altPartNumber: form.altPartNumber?.trim() || null,
        description: form.description || null,
        manufacturer: form.manufacturer || null,
        aircraftPlatform: form.aircraftPlatform || null,
        qty: form.qty,
        uom: form.uom || 'EA',
        conditionRequested: form.conditionRequested || null,
        certRequested: form.certRequested || null,
        isAog: form.isAog,
        notes: form.notes || null,
      };
      if (mode === 'edit') {
        if (!initial?.publicId) throw new Error('No line id to edit');
        return api(`/api/rfqs/line-items/${initial.publicId}`, {
          method: 'PATCH',
          tokenGetter: () => getToken(),
          body: JSON.stringify(body),
        });
      }
      return api(`/api/rfqs/${rfqId}/line-items`, {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      onClose();
    },
  });

  // Two-click delete confirmation lives inside this modal (edit mode only). The
  // server will hard-reject if any downstream artefact references the line —
  // that error surfaces in the same errorMsg slot the save mutation uses.
  // (Confirm state is reset together with form re-seeding in the useEffect above.)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useMutation({
    mutationFn: async () => {
      if (!initial?.publicId) throw new Error('No line id to delete');
      return api(`/api/rfqs/line-items/${initial.publicId}`, {
        method: 'DELETE',
        tokenGetter: () => getToken(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
      onClose();
    },
  });

  if (!open) return null;

  const canSave = form.partNumber.trim().length > 0 && form.qty >= 1 && !save.isPending && !del.isPending;
  // Surface whichever mutation most recently errored (delete error is the more
  // important one to see — it carries the "blocked by N quotes…" message).
  const errorMsg =
    (del.error as { message?: string } | null)?.message
    ?? (save.error as { message?: string } | null)?.message
    ?? null;

  function update<K extends keyof LineItemInitial>(k: K, v: LineItemInitial[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" {...dismiss}>
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">
              {mode === 'edit' ? `Edit line ${initial?.lineNumber ?? ''}` : 'Add line item'}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {mode === 'edit'
                ? 'Update the part info. Existing vendor & customer quotes for this line stay linked — review them if the part number or quantity changes.'
                : 'Add a new part to this RFQ. Stage defaults to received; the new line is assigned the next line number.'}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Part number" required>
              <input
                type="text"
                value={form.partNumber}
                onChange={(e) => update('partNumber', e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                placeholder="e.g. 4313500-00-6600"
                autoFocus
              />
            </Field>
            <Field label="Alt part number">
              <input
                type="text"
                value={form.altPartNumber ?? ''}
                onChange={(e) => update('altPartNumber', e.target.value || null)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                placeholder="OEM or vendor alt"
              />
            </Field>
          </div>

          <div className="grid grid-cols-[1fr_120px_120px] gap-3">
            <Field label="Description">
              <input
                type="text"
                value={form.description ?? ''}
                onChange={(e) => update('description', e.target.value || null)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                placeholder="e.g. Hydraulic steering motor"
              />
            </Field>
            <Field label="Qty" required>
              <input
                type="number"
                min={1}
                value={form.qty}
                onChange={(e) => update('qty', Math.max(1, Number(e.target.value) || 1))}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded text-right"
              />
            </Field>
            <Field label="UoM">
              <input
                type="text"
                value={form.uom}
                onChange={(e) => update('uom', e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                placeholder="EA"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Manufacturer">
              <input
                type="text"
                value={form.manufacturer ?? ''}
                onChange={(e) => update('manufacturer', e.target.value || null)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                placeholder="e.g. Boeing, Honeywell"
              />
            </Field>
            <Field label="Aircraft / platform">
              <input
                type="text"
                value={form.aircraftPlatform ?? ''}
                onChange={(e) => update('aircraftPlatform', e.target.value || null)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                placeholder="e.g. B747, A320 family"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Condition requested">
              <select
                value={form.conditionRequested ?? ''}
                onChange={(e) => update('conditionRequested', e.target.value || null)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              >
                <option value="">— Any —</option>
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Cert requested">
              <select
                value={form.certRequested ?? ''}
                onChange={(e) => update('certRequested', e.target.value || null)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              >
                <option value="">— Per OEM standard —</option>
                {CERTS.map((c) => <option key={c} value={c}>{CERT_LABEL[c]}</option>)}
              </select>
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.isAog}
              onChange={(e) => update('isAog', e.target.checked)}
            />
            <span>AOG (aircraft on ground)</span>
          </label>

          <Field label="Notes">
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => update('notes', e.target.value || null)}
              rows={3}
              className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded resize-y"
              placeholder="Sourcing notes, vendor pushback, condition specifics, etc."
            />
          </Field>

          {errorMsg && (
            <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded px-3 py-2">
              {errorMsg}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-border">
          {/* Delete (edit mode only). Two-click: first click arms, second click
              fires. Server hard-rejects with a 409 if anything downstream is
              attached — that message shows up in the error slot above. */}
          {mode === 'edit' && initial?.publicId ? (
            confirmDelete ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => del.mutate()}
                  disabled={del.isPending}
                  className="px-3 py-1.5 text-sm font-medium bg-destructive text-destructive-foreground rounded-md disabled:opacity-50"
                >
                  {del.isPending ? 'Deleting…' : 'Click again to confirm delete'}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={del.isPending}
                  className="px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={save.isPending || del.isPending}
                className="px-3 py-1.5 text-sm text-destructive border border-destructive/40 rounded-md hover:bg-destructive/5 disabled:opacity-50"
              >
                Delete line
              </button>
            )
          ) : <div />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">
              Cancel
            </button>
            <button
              onClick={() => save.mutate()}
              disabled={!canSave}
              className="px-4 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Add line')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-muted-foreground mb-1">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}
